/**
 * ResetPassword.tsx
 * Handles the password reset flow after user clicks the email link.
 * Supabase redirects to /reset-password with a token in the URL hash.
 * We let Supabase pick up the session from the URL, then let the user
 * set a new password.
 */

import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, KeyRound, CheckCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface ResetPasswordProps {
  onDone: () => void;
}

export default function ResetPassword({ onDone }: ResetPasswordProps) {
  const [password, setPassword]         = useState('');
  const [confirm, setConfirm]           = useState('');
  const [showPw, setShowPw]             = useState(false);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState<string | null>(null);
  const [done, setDone]                 = useState(false);
  const [sessionReady, setSessionReady] = useState(false);

  // Supabase embeds the recovery token in the URL hash.
  // We need to explicitly call getSession after the hash is processed,
  // AND listen for the PASSWORD_RECOVERY event.
  useEffect(() => {
    // First, let Supabase process the hash tokens in the URL
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) setSessionReady(true);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY' || (event === 'SIGNED_IN' && session)) {
        setSessionReady(true);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const { error: updateErr } = await supabase.auth.updateUser({ password });
      if (updateErr) throw new Error(updateErr.message);
      setDone(true);
    } catch (err: any) {
      setError(err.message || 'Could not update password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fafafa] flex items-center justify-center p-6">
      <div className="bg-white rounded-3xl shadow-xl border border-slate-100 p-8 w-full max-w-md">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 bg-orange-500 rounded-xl text-white">
            <KeyRound className="w-5 h-5" />
          </div>
          <div>
            <h2 className="font-display font-bold text-lg text-slate-900">Reset Password</h2>
            <p className="text-[11px] text-slate-400">Enter your new password below</p>
          </div>
        </div>

        {done ? (
          // ── Success state ────────────────────────────────────────────────
          <div className="text-center space-y-4">
            <div className="w-14 h-14 bg-emerald-50 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-emerald-500" />
            </div>
            <p className="font-semibold text-slate-900">Password updated!</p>
            <p className="text-xs text-slate-500">You can now sign in with your new password.</p>
            <button
              onClick={onDone}
              className="w-full py-2.5 bg-orange-500 hover:bg-orange-600 text-white font-semibold text-xs rounded-xl transition-all"
            >
              Go to Sign In
            </button>
          </div>
        ) : !sessionReady ? (
          // ── Waiting for token ────────────────────────────────────────────
          <div className="text-center py-8 space-y-3">
            <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="text-xs text-slate-400">Verifying reset link...</p>
            <p className="text-[10px] text-slate-300">
              If this takes too long, your link may have expired.{' '}
              <button onClick={onDone} className="text-orange-500 hover:underline">
                Go back
              </button>
            </p>
          </div>
        ) : (
          // ── New password form ────────────────────────────────────────────
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-[10px] uppercase font-bold tracking-wider text-slate-400 block mb-1.5">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showPw ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="w-full bg-white/60 border border-slate-200 rounded-xl px-3.5 py-2 pr-10 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPw(p => !p)}
                  className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600"
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase font-bold tracking-wider text-slate-400 block mb-1.5">
                Confirm Password
              </label>
              <input
                type={showPw ? 'text' : 'password'}
                placeholder="••••••••"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                className="w-full bg-white/60 border border-slate-200 rounded-xl px-3.5 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-all"
                required
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-100 rounded-xl p-3 text-red-700 text-xs">
                ⚠️ {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 active:scale-95 text-white font-semibold text-xs rounded-xl uppercase tracking-wider transition-all shadow-lg shadow-orange-500/20"
            >
              {loading ? 'Updating...' : 'Set New Password'}
            </button>

            <button
              type="button"
              onClick={onDone}
              className="w-full text-center text-xs text-slate-400 hover:text-slate-600 transition-colors"
            >
              Cancel — go back
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
